import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductWithErrors } from "./ImportExportProduct";
import { AppConfig } from "@/utils/AppConfig";
import { OptionGroup } from "@/types/ProductType";
import { GoTrash } from "react-icons/go";

interface TooltipProps {
  content: string | string[] | { text: string; price: string | null }[];
  children: React.ReactNode;
  isList?: boolean;
}

interface OptionsTooltipProps {
  content: OptionGroup[] | string[];
  children: React.ReactNode;
}

function formatMoneyDashes(value: number | string): JSX.Element {
  if (value === null || value === undefined || value === "") {
    return <span dir="rtl">-</span>;
  }

  const num =
    typeof value === "string"
      ? Number(
          (value.replace(/[^0-9.-]+/g, "") ?? "-").match(/^-?\d*\.?\d*$/)
            ? RegExp.lastMatch
            : NaN,
        )
      : value;

  const isNumber = !isNaN(num);

  return (
    <span dir="rtl">
      {isNumber
        ? `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `
        : value}{" "}
      {isNumber && AppConfig.currency.icon("currentcolor", 13)}
    </span>
  );
}

function formatMoneyString(value: number | string): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const num =
    typeof value === "string"
      ? Number(
          (value.replace(/[^0-9.-]+/g, "") ?? "-").match(/^-?\d*\.?\d*$/)
            ? RegExp.lastMatch
            : NaN,
        )
      : value;

  const isNumber = !isNaN(num);

  return isNumber
    ? `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : value.toString();
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  isList = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY - 5,
        left: rect.left + rect.width / 2 + window.scrollX,
      });
    }
  }, [isVisible]);

  if (
    !content ||
    (typeof content === "string" && content === "-") ||
    (Array.isArray(content) &&
      content.some((item) =>
        typeof item === "string" ? item === "-" : item.text === "-",
      ))
  ) {
    return <span>-</span>;
  }

  return (
    <div
      className="relative inline-block text-center"
      ref={triggerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <p className="max-w-[9rem] cursor-pointer overflow-hidden truncate">
        {children}
      </p>
      {isVisible && (
        <div
          className="fixed z-[1000] w-max max-w-[90dvw] rounded-lg bg-gray-300 p-3 text-sm text-black shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateX(-50%) translateY(-100%)",
          }}
        >
          {isList && Array.isArray(content) ? (
            <ul className="list-disc ps-4">
              {content.map((item, index) => {
                const text = typeof item === "string" ? item : item.text;
                const price = typeof item === "string" ? null : item.price;
                return (
                  <li key={index} className="mb-1 flex items-center">
                    <span className="flex items-center gap-1">
                      {text}
                      {price && (
                        <span
                          dir="ltr"
                          className="inline-flex items-center gap-1"
                        >
                          {" ("}
                          <span className="inline-flex items-center">
                            {AppConfig.currency.icon("currentcolor", 13)}
                          </span>
                          <span>{` ${price})`}</span>
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span>{typeof content === "string" ? content : ""}</span>
          )}
          <div className="absolute bottom-0 left-1/2 z-10 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 transform bg-gray-300"></div>
        </div>
      )}
    </div>
  );
};

const OptionsTooltip: React.FC<OptionsTooltipProps> = ({
  content,
  children,
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY - 5,
        left: rect.left + rect.width / 2 + window.scrollX,
      });
    }
  }, [isVisible]);

  if (
    !content ||
    (Array.isArray(content) &&
      (content.length === 0 ||
        (content.every((item) => typeof item === "string") &&
          content.includes("-"))))
  ) {
    return <span>-</span>;
  }
  return (
    <div
      className="relative inline-block text-center"
      ref={triggerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <p className="max-w-[9rem] cursor-pointer overflow-hidden truncate">
        {children}
      </p>
      {isVisible && (
        <div
          className="fixed z-[1000] w-max max-w-[90dvw] rounded-lg bg-gray-300 p-3 text-sm text-black shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateX(-50%) translateY(-100%)",
          }}
        >
          {Array.isArray(content) &&
          content.every(
            (item): item is OptionGroup =>
              typeof item === "object" && item !== null && "options" in item,
          ) ? (
            <ul className="list-disc ps-3">
              {content.map((group: OptionGroup, index: number) => (
                <li key={index} className="mb-2">
                  <span className="block w-full text-start font-semibold">
                    {locale === "en"
                      ? group.nameEN || group.nameAR
                      : group.nameAR || group.nameEN}
                  </span>
                  <ul className="list-circle">
                    {group.options.map((option, subIndex) => (
                      <li key={subIndex} className="mb-1 flex">
                        <span className="flex items-center gap-1 ps-1">
                          {locale === "en"
                            ? option.nameEN || option.nameAR
                            : option.nameAR || option.nameEN}
                          {option.price && (
                            <span
                              dir="ltr"
                              className="inline-flex items-center gap-1"
                            >
                              {" ( "}
                              <span className="inline-flex items-center">
                                {AppConfig.currency.icon("currentcolor", 13)}
                              </span>
                              <span>{` ${formatMoneyString(option.price)} )`}</span>
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <span>-</span>
          )}
          <div className="absolute bottom-0 left-1/2 z-10 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 transform bg-gray-300"></div>
        </div>
      )}
    </div>
  );
};

function ProductsExportTable({
  products,
  deleteRow,
  errors
}: {
  products: ProductWithErrors[];
  deleteRow: (index: number) => void;
  errors: string[]
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const hasErrors = errors.length > 0

  if (!products || products.length === 0) {
    return (
      <div className="w-full text-center text-lg">
        {t("previewTableHeaders.noProducts")}
      </div>
    );
  }

  return (
    <div className="thin-scrollbar overflow-x-auto py-1">
      <table className="min-w-full border-collapse border border-gray-300 text-center">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.number")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.nameAR")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.nameEN")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.descriptionAR")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.descriptionEN")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.price")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.quantity")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.calories")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.salt")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.caffeine")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.lowPrice")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.lowPriceEnd")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.addons")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.options")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.productCost")}
            </th>
            <th className="border border-gray-300 p-2">
              {t("previewTableHeaders.hidden")}
            </th>
            {/* delete Header */}
            {hasErrors && <th className="border-none bg-white"></th>}
          </tr>
        </thead>
        <tbody>
          {products.map((product, i) => (
            <tr key={product._id} className="hover:bg-gray-50">
              <td className="w-fit max-w-[10rem] border border-gray-300 p-2">
                {i + 1 || "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.nameAR
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <Tooltip content={product.nameAR ?? "-"}>
                  {product.nameAR ?? "-"}
                </Tooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.nameEN
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <Tooltip content={product.nameEN ?? "-"}>
                  {product.nameEN ?? "-"}
                </Tooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.descriptionAR
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <Tooltip content={product.descriptionAR ?? "-"}>
                  {product.descriptionAR ?? "-"}
                </Tooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.descriptionEN
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <Tooltip content={product.descriptionEN ?? "-"}>
                  {product.descriptionEN ?? "-"}
                </Tooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.price
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {formatMoneyDashes(product.price || "") ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.quantity
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.quantity === "infinite"
                  ? t("previewTableHeaders.unlimited")
                  : (product.quantity ?? "-")}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.calories
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.calories ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.salt
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.salt ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.caffeine
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.caffeine ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.lowPrice
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.lowPrice ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.lowPriceEndDate
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.lowPriceEndDate
                  ? new Date(
                      product.lowPriceEndDate * 1000,
                    ).toLocaleDateString()
                  : "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.addons?.length
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <Tooltip
                  content={
                    product.addons?.length
                      ? product.addons.map((addon) => ({
                          text:
                            locale === "en"
                              ? addon.nameEN || addon.nameAR
                              : addon.nameAR || addon.nameEN,
                          price: addon.price
                            ? formatMoneyString(addon.price)
                            : null,
                        }))
                      : ["-"]
                  }
                  isList={true}
                >
                  {product.addons?.length
                    ? product.addons
                        .map((addon) =>
                          locale === "en"
                            ? addon.nameEN || addon.nameAR
                            : addon.nameAR || addon.nameEN,
                        )
                        .join(", ")
                    : "-"}
                </Tooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.options?.length
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                <OptionsTooltip
                  content={product.options?.length ? product.options : ["-"]}
                >
                  {product.options?.length
                    ? product.options
                        .map((opt) =>
                          locale === "en"
                            ? opt.nameEN || opt.nameAR
                            : opt.nameAR || opt.nameEN,
                        )
                        .join(", ")
                    : "-"}
                </OptionsTooltip>
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.productCost
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {formatMoneyDashes(product.productCost || "") ?? "-"}
              </td>
              <td
                className={`w-fit max-w-[10rem] border ${
                  product?.errors?.hidden
                    ? "border-2 border-red-400 font-semibold text-red-500"
                    : "border-gray-300"
                } p-2`}
              >
                {product.hidden
                  ? t("previewTableHeaders.yes")
                  : t("previewTableHeaders.no")}
              </td>
              {hasErrors && <td className="w-fit max-w-[10rem] border border-gray-300 p-2">
                {product.errors !== null ? (
                  <button onClick={() => deleteRow(i)}>
                    <GoTrash
                      size={20}
                      className="text-red-400 hover:text-red-600"
                    />
                  </button>
                ) : null}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ProductsExportTable;
